import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CButton, CLabel, CModal, CModalBody, CModalFooter, CModalHeader,
  CModalTitle, CSelect, CTooltip,
} from "@coreui/react";
import { getItem } from "../../api/generics";
import { getObjectMinioUrl } from "../../minio/urls";
import { SUCCESS } from "../../utils/constants/tags";
import {
  AUTO_INVOICES_RETRIEVE, CREDIT_NOTES_RETRIEVE, DEBIT_NOTES_RETRIEVE,
  INVOICES_RETRIEVE, RECEIPTS_RETRIEVE, REMISSION_NOTES_RETRIEVE,
} from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import { errorAlert } from "../utils/messages";

export type PrintableDocumentKind =
  | "invoice" | "credit-note" | "debit-note" | "remission-note" | "auto-invoice" | "receipt";

/** Endpoint, permission and Spanish naming for each document that can be printed. */
const PRINTABLE_DOCUMENTS: Record<PrintableDocumentKind, {
  endpoint: string; permissionName: string; label: string; filePrefix: string; ticket?: boolean;
}> = {
  invoice: { endpoint: "/invoices", permissionName: INVOICES_RETRIEVE, label: "factura", filePrefix: "factura", ticket: true },
  "credit-note": { endpoint: "/credit_notes", permissionName: CREDIT_NOTES_RETRIEVE, label: "nota de crédito", filePrefix: "nota-de-credito", ticket: true },
  "debit-note": { endpoint: "/debit_notes", permissionName: DEBIT_NOTES_RETRIEVE, label: "nota de débito", filePrefix: "nota-de-debito" },
  "remission-note": { endpoint: "/remission_notes", permissionName: REMISSION_NOTES_RETRIEVE, label: "nota de remisión", filePrefix: "nota-de-remision" },
  "auto-invoice": { endpoint: "/auto_invoices", permissionName: AUTO_INVOICES_RETRIEVE, label: "autofactura", filePrefix: "autofactura" },
  receipt: { endpoint: "/receipts", permissionName: RECEIPTS_RETRIEVE, label: "recibo", filePrefix: "recibo" },
};

const LAYOUT_STORAGE_KEY = "documentPrintLayout";
const DEFAULT_LAYOUT = "two-copies";

const readStoredLayout = (ticketAvailable: boolean) => {
  try {
    const stored = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (stored === "single" || stored === "two-copies" || (stored === "ticket" && ticketAvailable)) return stored;
  } catch (e) {
    // Storage may be unavailable (private mode, blocked site data).
  }
  return DEFAULT_LAYOUT;
};

const storeLayout = (layout: string) => {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
  } catch (e) {
    // The choice is only a convenience; printing works without it.
  }
};

const DocumentPrintButton = ({ kind, documentId }: { kind: PrintableDocumentKind; documentId: number }) => {
  const { endpoint, permissionName, label, filePrefix, ticket: ticketAvailable = false } = PRINTABLE_DOCUMENTS[kind];
  const [show, setShow] = useState(false);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [pdfUrl, setPdfUrl] = useState<string>();
  const [ticketHtml, setTicketHtml] = useState<string>();
  const paginateTicket = useRef<(doc: Document) => void>();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<{ message: string; detail: string }>();
  const [attempt, setAttempt] = useState(0);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    setPdfUrl(undefined);
    setTicketHtml(undefined);
    setLoaded(false);
    setError(undefined);
    if (!show) return;
    const controller = new AbortController();
    let objectUrl: string | undefined;
    const loadTicket = async () => {
      let failureMessage = "No se pudo obtener el enlace del XML. Verificá tu conexión y tu sesión.";
      try {
        const result = await getItem<{ url: string }>(`${endpoint}/${documentId}/xml/`);
        if (controller.signal.aborted) return;
        if (result.status !== SUCCESS || !result.data) {
          throw new Error(result.detail || `Estado de la solicitud: ${result.status}`);
        }
        failureMessage = "No se pudo descargar el XML para preparar el ticket. Revisá el error de red en la consola del navegador.";
        const response = await fetch(getObjectMinioUrl(result.data.url), { signal: controller.signal });
        if (!response.ok) throw new Error(`Descarga del XML: HTTP ${response.status}`);
        const xml = await response.text();
        if (controller.signal.aborted) return;
        failureMessage = "No se pudo armar el ticket a partir del XML del documento. Probá otra distribución y consultá los detalles del error.";
        // Load ticket rendering only when this print layout is requested.
        const ticket = await import("../../utils/print-ticket");
        const html = ticket.createTicketHtml(xml);
        if (controller.signal.aborted) return;
        paginateTicket.current = ticket.paginateTicket;
        setTicketHtml(html);
      } catch (e) {
        if (!controller.signal.aborted) {
          setError({
            message: failureMessage,
            detail: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
          });
        }
      }
    };
    const load = async () => {
      let failureMessage = "No se pudo obtener el enlace del PDF. Verificá tu conexión y tu sesión.";
      try {
        const result = await getItem<{ url: string }>(`${endpoint}/${documentId}/pdf/`);
        if (controller.signal.aborted) return;
        if (result.status !== SUCCESS || !result.data) {
          throw new Error(result.detail || `Estado de la solicitud: ${result.status}`);
        }
        failureMessage = "No se pudo descargar el PDF para preparar la impresión. Revisá el error de red en la consola del navegador.";
        const response = await fetch(getObjectMinioUrl(result.data.url), { signal: controller.signal });
        if (!response.ok) throw new Error(`Descarga del PDF: HTTP ${response.status}`);
        const original = await response.arrayBuffer();
        if (controller.signal.aborted) return;
        failureMessage = "No se pudo crear la distribución de dos copias. Probá el formato original y consultá los detalles del error.";
        // Load PDF processing only when this print layout is requested.
        const bytes = layout === "two-copies"
          ? await (await import("../../utils/print-pdf")).createTwoCopyPrintPdf(new Uint8Array(original))
          : new Uint8Array(original);
        failureMessage = "No se pudo abrir la vista previa del PDF en este navegador.";
        const blob = new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch (e) {
        if (!controller.signal.aborted) {
          setError({
            message: failureMessage,
            detail: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
          });
        }
      }
    };
    if (layout === "ticket") loadTicket(); else load();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [show, layout, endpoint, documentId, attempt]);

  useEffect(() => {
    if (show) setLayout(readStoredLayout(ticketAvailable));
  }, [show, ticketAvailable]);

  const onTicketLoad = () => {
    const doc = frame.current?.contentDocument;
    if (doc && paginateTicket.current) paginateTicket.current(doc);
    setLoaded(true);
  };

  const print = () => {
    try {
      if (!frame.current?.contentWindow) throw new Error("Missing preview");
      frame.current.contentWindow.focus();
      frame.current.contentWindow.print();
    } catch (e) {
      errorAlert("Usá el botón de impresión de la vista previa o descargá el PDF para imprimirlo.");
    }
  };

  return (
    <PermissionRequiredComponent permissionName={permissionName}>
      <CTooltip content="Imprimir">
        <CButton color="secondary" className="document-print-button" aria-label={`Imprimir ${label}`} onClick={() => setShow(true)}>
          <i className="fa fa-print" aria-hidden="true" />
        </CButton>
      </CTooltip>
      {createPortal(<CModal show={show} onClose={() => setShow(false)} size="xl">
        <CModalHeader closeButton><CModalTitle>Imprimir {label}</CModalTitle></CModalHeader>
        <CModalBody>
          <CLabel htmlFor={`print-layout-${kind}-${documentId}`}>Distribución</CLabel>
          <CSelect id={`print-layout-${kind}-${documentId}`} value={layout} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setPdfUrl(undefined);
            setTicketHtml(undefined);
            setLoaded(false);
            setLayout(e.target.value);
            storeLayout(e.target.value);
          }}>
            <option value="single">Una copia por hoja (formato original)</option>
            <option value="two-copies">Dos copias por hoja, lado a lado (A4 horizontal)</option>
            {ticketAvailable && <option value="ticket">Ticket 76 mm (impresora de rollo)</option>}
          </CSelect>
          <p className="mt-3">
            {layout === "two-copies"
              ? "Cada página se repite a ambos lados de la línea de corte. Al imprimir, seleccioná A4 horizontal, una página por hoja y una copia."
              : layout === "ticket"
                ? "Ticket de 76 mm de ancho armado con los datos del documento electrónico. Al imprimir, elegí la impresora de tickets, márgenes \"Ninguno\" y escala 100%. El corte del papel lo maneja la configuración de la impresora."
                : "Se imprimirá el PDF original sin modificar su distribución."}
          </p>
          {pdfUrl && <p>Si tu navegador no muestra la vista previa o no abre la impresión, descargá el PDF para imprimirlo.</p>}
          {error ? <div role="alert" className="text-danger">
            <p>{error.message}</p>
            <details><summary>Detalles del error</summary><pre style={{ whiteSpace: "pre-wrap" }}>{error.detail}</pre></details>
          </div>
            : pdfUrl ? <iframe ref={frame} src={pdfUrl} title="Vista previa de impresión" onLoad={() => setLoaded(true)} style={{ width: "100%", height: "60vh", border: 0 }} />
            : ticketHtml ? <iframe ref={frame} srcDoc={ticketHtml} title="Vista previa de impresión" onLoad={onTicketLoad} style={{ width: "100%", height: "60vh", border: 0 }} />
            : <p role="status">Preparando vista previa...</p>}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setShow(false)}>Cerrar</CButton>
          {error && <CButton color="primary" onClick={() => setAttempt(value => value + 1)}>Reintentar</CButton>}
          {pdfUrl && <a className="btn btn-secondary" href={pdfUrl} download={`${filePrefix}-${documentId}-${layout}.pdf`}>Descargar PDF</a>}
          <CButton color="primary" disabled={!(pdfUrl || ticketHtml) || !loaded} onClick={print}>Imprimir</CButton>
        </CModalFooter>
      </CModal>, document.body)}
    </PermissionRequiredComponent>
  );
};

export default DocumentPrintButton;
